/**
 * 私信（D70–D72）。v1 用轮询：
 *
 * - 未读红点：60 秒一次 + 窗口聚焦时，和通知一样；
 * - 会话列表：停在列表页时 30 秒一次；
 * - 打开的对话：5 秒问一次「有没有比我手上最新那条更新的」，只拉增量，
 *   拿到了就塞进缓存最前面 —— 不整页重取，列表不会跳。
 *
 * 消息缓存是 useInfiniteQuery，第一页是**最新**的一批（新的在前），
 * 渲染时整体翻转成时间正序。
 */

import type { InfiniteData } from '@tanstack/react-query'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { conversationsApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { Conversation, DirectMessage, Page } from '@/api/types'

const UNREAD_POLL_MS = 60_000
const LIST_POLL_MS = 30_000
const THREAD_POLL_MS = 5_000

type MessagePages = InfiniteData<Page<DirectMessage>, string | undefined>

export function useDmUnreadCount(enabled = true) {
  return useQuery({
    queryKey: keys.dmUnread(),
    queryFn: () => conversationsApi.unreadCount(),
    enabled,
    refetchInterval: UNREAD_POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  })
}

export function useConversations() {
  return useInfiniteQuery({
    queryKey: keys.conversations(),
    queryFn: ({ pageParam }) => conversationsApi.list(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: Page<Conversation>) => last.next_cursor ?? undefined,
    refetchInterval: LIST_POLL_MS,
  })
}

export function useConversation(id: string) {
  return useQuery({
    queryKey: keys.conversation(id),
    queryFn: () => conversationsApi.get(id),
    enabled: Boolean(id),
  })
}

export function useConversationMessages(id: string) {
  return useInfiniteQuery({
    queryKey: keys.conversationMessages(id),
    queryFn: ({ pageParam }) => conversationsApi.messages(id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: Page<DirectMessage>) => last.next_cursor ?? undefined,
    enabled: Boolean(id),
    // 新消息靠下面的增量轮询进来，不要整页自动重取（会把向上翻过的页都重拉一遍）
    staleTime: Infinity,
  })
}

/** 把新消息放进缓存最前面（第一页是最新的，新的在前），去掉已经有的 */
export function prependMessages(
  client: ReturnType<typeof useQueryClient>,
  id: string,
  incoming: DirectMessage[],
) {
  if (incoming.length === 0) return
  client.setQueryData<MessagePages>(keys.conversationMessages(id), (data) => {
    if (!data || data.pages.length === 0) return data
    const [first, ...rest] = data.pages
    const known = new Set(data.pages.flatMap((page) => page.items.map((m) => m.id)))
    // 去重（缓存里已有的、这一批里重复的）后按 id 从新到旧，放到最前面
    const fresh = new Map<string, DirectMessage>()
    for (const message of incoming) if (!known.has(message.id)) fresh.set(message.id, message)
    if (fresh.size === 0) return data
    const newestFirst = [...fresh.values()].sort((a, b) => Number(b.id) - Number(a.id))
    return { ...data, pages: [{ ...first, items: [...newestFirst, ...first.items] }, ...rest] }
  })
}

/**
 * 对话打开期间，定时拉比 `latestId` 更新的消息。
 * 还没有任何消息的会话（两个人同时打开了空会话）就拉第一页。
 */
export function usePollNewMessages(id: string, latestId: string | undefined, enabled: boolean) {
  const client = useQueryClient()
  useEffect(() => {
    if (!enabled) return
    const timer = window.setInterval(async () => {
      if (document.visibilityState !== 'visible') return
      try {
        const page = latestId
          ? await conversationsApi.messagesAfter(id, latestId)
          : await conversationsApi.messages(id)
        prependMessages(client, id, page.items)
      } catch {
        // 轮询失败不打扰人：下一轮再试
      }
    }, THREAD_POLL_MS)
    return () => window.clearInterval(timer)
  }, [client, id, latestId, enabled])
}

/**
 * 发一条。发完顺手把「发送前手上最新那条」之后的都补上：对方在我打字的时候发来的，
 * id 比我这条小，轮询以我这条为起点就永远拉不到了。
 */
export function useSendMessage(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ content, after }: { content: string; after?: string }) => {
      const message = await conversationsApi.send(id, content)
      const missed = after ? (await conversationsApi.messagesAfter(id, after)).items : []
      return [...missed, message]
    },
    onSuccess: (messages) => {
      prependMessages(client, id, messages)
      client.invalidateQueries({ queryKey: keys.conversations() })
    },
  })
}

export function useMarkConversationRead(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (upToId?: string) => conversationsApi.markRead(id, upToId),
    onSuccess: (result) => {
      client.setQueryData(keys.dmUnread(), result.unread)
      client.setQueryData<Conversation>(keys.conversation(id), (data) =>
        data ? { ...data, unread: 0 } : data,
      )
      client.invalidateQueries({ queryKey: keys.conversations() })
    },
  })
}

export function useStartConversation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (username: string) => conversationsApi.start(username),
    onSuccess: (conversation) => {
      client.setQueryData(keys.conversation(conversation.id), conversation)
    },
  })
}
